import { Box, Center, Text, VStack } from "@chakra-ui/layout";
import { FormControl, FormLabel } from "@chakra-ui/form-control";
import { Input } from "@chakra-ui/input";
import {
  Button,
  Collapse,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  Wrap,
} from "@chakra-ui/react";
import { Spinner } from "@chakra-ui/spinner";
import { Table, Tbody, Td, Th, Thead, Tr } from "@chakra-ui/table";
import { Menu, MenuButton, MenuItem, MenuList } from "@chakra-ui/menu";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "../Card/Card";
import { useSearchInputState } from "../../hooks/useSearchInputState";
import { usePaginatedApiRequest } from "../../hooks/usePaginatedApiRequest";
import { ErrorMessage } from "../ErrorMessage/ErrorMessage";
import { useModalState } from "../../hooks/useModalState";
import { PointsRequestModal } from "../../components/PointsRequestModal/PointsRequestModal";
import {
  CardIcon,
  DeleteIcon,
  FilterIcon,
  MoreHorizontal,
  PinIcon,
  PinFilledIcon,
  PlusIcon,
  TransferIcon,
  TrashIcon,
  UserIcon,
  UserPlusIcon,
} from "../../styles/icons";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { InvitationModal } from "../InvitationModal/InvitationModal";
import { usePermissions } from "../../hooks/usePermissions";
import { TransferPointsModal } from "../TransferPointsModal/TransferPointsModal";
import { CherryPayCardModal } from "../CherryPayCardModal/CherryPayCardModal";
import DateUtil from "../../util/DateUtil";
import { emptyStrToNull } from "../../util/StringUtil";
import { CherryPayApi } from "../../api/models";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import { useApiUpdateRequest } from "../../hooks/useApiUpdateRequest";
import { useBusinessContext } from "../../context/ModelContext";
import { useToast } from "../../hooks/useToast";
import { useApiRequest } from "../../hooks/useApiRequest";
import { Select } from "@chakra-ui/react";
import ReactSelect from "react-select";
import { FilterSelectionDialog } from "../FilterSelectionDialog/FilterSelectionDialog";
import {
  ADVANCED_FILTER_OPTIONS,
  loadPinnedFilters,
  savePinnedFilters,
  loadFilterValues,
  saveFilterValues,
} from "./filterTypes";
import { AdvancedFilterInput } from "./AdvancedFilterInput";

export const Members = ({ businessId }: { businessId: string }) => {
  const {
    query: membershipNumber,
    debouncedQuery: debouncedMembershipNumber,
    onChange: onMembershipNumberChange,
    isDebouncing: isMembershipNumberDebouncing,
    setQuery: setMembershipNumber,
  } = useSearchInputState("");
  const {
    query: firstName,
    debouncedQuery: debouncedFirstName,
    onChange: onFirstNameChange,
    isDebouncing: isFirstNameDebouncing,
    setQuery: setFirstName,
  } = useSearchInputState("");
  const {
    query: lastName,
    debouncedQuery: debouncedLastName,
    onChange: onLastNameChange,
    isDebouncing: isLastNameDebouncing,
    setQuery: setLastName,
  } = useSearchInputState("");
  const {
    query: dateOfBirth,
    debouncedQuery: debouncedDateOfBirth,
    onChange: onDateOfBirthChange,
    isDebouncing: isDateOfBirthDebouncing,
    setQuery: setDateOfBirth,
  } = useSearchInputState("");

  const [
    canAwardPoints,
    canInviteMemberToCard,
    canInviteMemberToApp,
    canTransferPointsToMoney,
  ] = usePermissions(
    "Loyalty.CreditPoints",
    "Member.InviteToCard",
    "Member.InviteToApp",
    "Cardholder.PointsToMoney"
  );

  const [filterQuery, setFilterQuery] = useState<string>();
  const [enableResetButton, setEnableResetButton] = useState<boolean>(false);
  const navigate = useNavigate();
  const { showErrorToast, showSuccessToast } = useToast();

  // New filter system state
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());
  const [pinnedFilters, setPinnedFilters] = useState<Set<string>>(() =>
    loadPinnedFilters(businessId)
  );
  const [advancedFilterValues, setAdvancedFilterValues] = useState<any>(() => {
    const stored = loadFilterValues(businessId);
    return stored.advancedFilters || {};
  });
  const filterSelectionDialog = useDisclosure();
  const pinConfirmationDialog = useDisclosure();
  const [pendingFiltersToAdd, setPendingFiltersToAdd] = useState<Set<string>>(
    new Set()
  );
  const [isFilterSectionCollapsed, setIsFilterSectionCollapsed] =
    useState(false);

  // Load extended field definitions
  const { data: extendedFieldDefinitions, isLoading: isLoadingExtendedFields } =
    useApiRequest(
      (apiClient) => apiClient.getExtendedFieldDefinitions(businessId),
      [businessId]
    );

  // Load advanced filter options
  const { data: birthMonthOptions } = useApiRequest(
    (apiClient) =>
      apiClient.getEnumConfigurations(businessId, "CalendarMonths" as any),
    [businessId]
  );

  const { data: genderOptions } = useApiRequest(
    (apiClient) => apiClient.getMemberDataOptions(businessId, "Gender" as any),
    [businessId]
  );

  const { data: statusOptions } = useApiRequest(
    (apiClient) => apiClient.getMemberDataOptions(businessId, "Status" as any),
    [businessId]
  );

  const { data: memberLevelOptions } = useApiRequest(
    (apiClient) =>
      apiClient.getMemberDataOptions(businessId, "MemberLevel" as any),
    [businessId]
  );

  const { data: memberLevelDescriptionOptions } = useApiRequest(
    (apiClient) =>
      apiClient.getMemberDataOptions(
        businessId,
        "MemberLevelDescription" as any
      ),
    [businessId]
  );

  const { data: membershipTypeOptions } = useApiRequest(
    (apiClient) =>
      apiClient.getMemberDataOptions(businessId, "MembershipType" as any),
    [businessId]
  );

  const { data: membershipTypeDescriptionOptions } = useApiRequest(
    (apiClient) =>
      apiClient.getMemberDataOptions(
        businessId,
        "MembershipTypeDescription" as any
      ),
    [businessId]
  );

  // State for extended field search values - load from session storage
  const [extendedFieldSearchValues, setExtendedFieldSearchValues] = useState<{
    [key: string]: string | string[];
  }>(() => {
    const stored = loadFilterValues(businessId);
    return stored.extendedFields || {};
  });

  // Debounced extended field search values
  const [
    debouncedExtendedFieldSearchValues,
    setDebouncedExtendedFieldSearchValues,
  ] = useState<{
    [key: string]: string | string[];
  }>({});

  // Debounce extended field search values
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedExtendedFieldSearchValues(extendedFieldSearchValues);
    }, 500);
    return () => clearTimeout(timer);
  }, [extendedFieldSearchValues]);

  // Debounced advanced filter values
  const [debouncedAdvancedFilterValues, setDebouncedAdvancedFilterValues] =
    useState<any>({});

  // Debounce advanced filter values
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAdvancedFilterValues(advancedFilterValues);
    }, 500);
    return () => clearTimeout(timer);
  }, [advancedFilterValues]);

  // Convert advanced filter values and extended field multi-select values to query string and update filterQuery
  useEffect(() => {
    const generateQueryFromAdvancedFilters = () => {
      const params: string[] = [];

      // Process advanced filter values - only for active filters
      Object.entries(debouncedAdvancedFilterValues).forEach(([key, value]) => {
        // Only process if this filter is active (visible)
        if (!activeFilters.has(key)) {
          return;
        }

        if (value !== undefined && value !== null && value !== "") {
          // Map filter IDs to query parameter names
          const paramName = key.charAt(0).toUpperCase() + key.slice(1);

          // Handle arrays (multi-select values)
          if (Array.isArray(value)) {
            value.forEach((v) => {
              if (v && v !== "") {
                params.push(`${paramName}=${encodeURIComponent(v)}`);
              }
            });
          } else {
            // Handle single values
            params.push(`${paramName}=${encodeURIComponent(value as string)}`);
          }
        }
      });

      // Process extended field multi-select values - only for active filters
      Object.entries(debouncedExtendedFieldSearchValues).forEach(
        ([key, value]) => {
          // Only process if this filter is active (visible)
          if (!activeFilters.has(key)) {
            return;
          }

          if (Array.isArray(value) && value.length > 0) {
            // This is a multi-select extended field, add to filter query
            value.forEach((v) => {
              if (v && v !== "") {
                params.push(`${key}=${encodeURIComponent(v)}`);
              }
            });
          }
        }
      );

      const newFilterQuery =
        params.length === 0 ? undefined : "?" + params.join("&");

      // Only update if the query has actually changed
      setFilterQuery((prevQuery) => {
        if (prevQuery === newFilterQuery) {
          return prevQuery;
        }
        return newFilterQuery;
      });
    };

    generateQueryFromAdvancedFilters();
  }, [
    debouncedAdvancedFilterValues,
    debouncedExtendedFieldSearchValues,
    activeFilters,
  ]);

  // Initialize active filters from pinned filters on mount
  useEffect(() => {
    setActiveFilters(new Set(pinnedFilters));
  }, []);

  // Load core field values from session storage on mount
  useEffect(() => {
    const stored = loadFilterValues(businessId);
    if (stored.coreFields) {
      if (stored.coreFields.membershipNumber) {
        setMembershipNumber(stored.coreFields.membershipNumber);
      }
      if (stored.coreFields.firstName) {
        setFirstName(stored.coreFields.firstName);
      }
      if (stored.coreFields.lastName) {
        setLastName(stored.coreFields.lastName);
      }
      if (stored.coreFields.dateOfBirth) {
        setDateOfBirth(stored.coreFields.dateOfBirth);
      }
    }
  }, [businessId]);

  // Clean up filter values for non-active filters on mount
  useEffect(() => {
    // Remove advanced filter values that aren't in pinned filters
    setAdvancedFilterValues((prev: any) => {
      const cleaned = { ...prev };
      let hasChanges = false;

      Object.keys(cleaned).forEach((key) => {
        if (!pinnedFilters.has(key)) {
          delete cleaned[key];
          hasChanges = true;
        }
      });

      return hasChanges ? cleaned : prev;
    });

    // Remove extended field values that aren't in pinned filters
    setExtendedFieldSearchValues((prev) => {
      const cleaned = { ...prev };
      let hasChanges = false;

      Object.keys(cleaned).forEach((key) => {
        if (!pinnedFilters.has(key)) {
          delete cleaned[key];
          hasChanges = true;
        }
      });

      return hasChanges ? cleaned : prev;
    });
  }, []); // Run once on mount

  // Clean up filter values when activeFilters changes (filters removed during session)
  const isFirstRender = useRef(true);
  useEffect(() => {
    // Skip cleanup on first render (handled by mount effect above)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Remove advanced filter values that aren't in activeFilters
    setAdvancedFilterValues((prev: any) => {
      const cleaned = { ...prev };
      let hasChanges = false;

      Object.keys(cleaned).forEach((key) => {
        if (!activeFilters.has(key)) {
          delete cleaned[key];
          hasChanges = true;
        }
      });

      return hasChanges ? cleaned : prev;
    });

    // Remove extended field values that aren't in activeFilters
    setExtendedFieldSearchValues((prev) => {
      const cleaned = { ...prev };
      let hasChanges = false;

      Object.keys(cleaned).forEach((key) => {
        if (!activeFilters.has(key)) {
          delete cleaned[key];
          hasChanges = true;
        }
      });

      return hasChanges ? cleaned : prev;
    });

    // Remove filters from pinnedFilters if they're no longer in activeFilters
    setPinnedFilters((prev) => {
      const cleaned = new Set(prev);
      let hasChanges = false;

      prev.forEach((filterId) => {
        if (!activeFilters.has(filterId)) {
          cleaned.delete(filterId);
          hasChanges = true;
        }
      });

      return hasChanges ? cleaned : prev;
    });
  }, [activeFilters]);

  // Persist pinned filters to localStorage
  useEffect(() => {
    savePinnedFilters(businessId, pinnedFilters);
  }, [pinnedFilters, businessId]);

  // Persist all filter values to sessionStorage
  useEffect(() => {
    saveFilterValues(businessId, {
      advancedFilters: advancedFilterValues,
      extendedFields: extendedFieldSearchValues,
      coreFields: {
        membershipNumber,
        firstName,
        lastName,
        dateOfBirth,
      },
    });
  }, [
    advancedFilterValues,
    extendedFieldSearchValues,
    membershipNumber,
    firstName,
    lastName,
    dateOfBirth,
    businessId,
  ]);

  // Filter out array values from extendedFieldSearchValues for API call
  // Use ref to maintain stable object reference when content hasn't changed
  const singleValueExtendedFieldFiltersRef = useRef<{ [key: string]: string }>(
    {}
  );
  const singleValueExtendedFieldFilters = useMemo(() => {
    const filtered: { [key: string]: string } = {};
    Object.entries(debouncedExtendedFieldSearchValues).forEach(
      ([key, value]) => {
        // Only process if this filter is active (visible)
        if (!activeFilters.has(key)) {
          return;
        }

        if (!Array.isArray(value) && value) {
          // Find the field definition to check if it's a DateTime field
          const fieldDef = extendedFieldDefinitions?.find((f) => f.id === key);

          if (fieldDef?.FieldType === "DateTime") {
            // Convert DateTime values to UTC ISO format
            const dateValue = new Date(value);
            if (!isNaN(dateValue.getTime())) {
              filtered[key] = dateValue.toISOString();
            }
          } else {
            // Keep other field types as-is
            filtered[key] = value;
          }
        }
      }
    );

    // Only return new object if content has changed
    const currentJson = JSON.stringify(filtered);
    const previousJson = JSON.stringify(
      singleValueExtendedFieldFiltersRef.current
    );

    if (currentJson !== previousJson) {
      singleValueExtendedFieldFiltersRef.current = filtered;
    }

    return singleValueExtendedFieldFiltersRef.current;
  }, [
    debouncedExtendedFieldSearchValues,
    extendedFieldDefinitions,
    activeFilters,
  ]);

  const {
    items: members,
    isLoading,
    isLoadingPage,
    error,
    refresh,
    fetchNextPage,
  } = usePaginatedApiRequest(
    // Request the first page
    (apiClient, signal) =>
      apiClient.searchMembersQuery(
        businessId,
        undefined,
        debouncedMembershipNumber,
        debouncedFirstName,
        debouncedLastName,
        debouncedDateOfBirth,
        filterQuery,
        undefined,
        undefined,
        singleValueExtendedFieldFilters,
        signal
      ),
    // Request subsequent pages
    (apiClient, continuationToken, signal) =>
      apiClient.searchMembersQuery(
        businessId,
        undefined,
        debouncedMembershipNumber,
        debouncedFirstName,
        debouncedLastName,
        debouncedDateOfBirth,
        filterQuery,
        continuationToken,
        undefined,
        singleValueExtendedFieldFilters,
        signal
      ),
    [
      businessId,
      debouncedFirstName,
      debouncedLastName,
      debouncedMembershipNumber,
      debouncedDateOfBirth,
      filterQuery,
      singleValueExtendedFieldFilters,
    ]
  );

  const business = useBusinessContext();
  const addPointsModalState = useModalState<{
    memberId: string;
    type: "add-points" | "transfer-points-to-cpc";
  }>();
  const invitationModalState = useModalState<{
    memberId: string;
    type: "digital-membership" | "cherry-pay-card";
    cpcType?: "instantgift" | "reloadable";
  }>();
  const { confirmationElement, showConfirmationDialog } =
    useConfirmationDialog();

  const deleteMemberRequest = useApiUpdateRequest(
    (apiClient, memberId: string) =>
      apiClient.deleteHubMembershipMember(businessId, memberId),
    [businessId]
  );

  const onDeleteMember = useCallback(
    (member: CherryPayApi.Member) => {
      showConfirmationDialog(
        "Delete Member?",
        "Are you sure you wish to delete this member?",
        "Delete"
      ).then(({ ok }) => {
        if (ok) {
          deleteMemberRequest.request(member.id);
        }
      });
    },
    [deleteMemberRequest.request, showConfirmationDialog]
  );

  // Check if there are any extended field filters active
  const hasExtendedFieldFilters = useMemo(
    () =>
      Object.values(extendedFieldSearchValues).some((value) => {
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return value !== "";
      }),
    [extendedFieldSearchValues]
  );

  useEffect(() => {
    const hasFilters =
      filterQuery ||
      firstName ||
      lastName ||
      membershipNumber ||
      dateOfBirth ||
      hasExtendedFieldFilters ||
      activeFilters.size > 0;
    setEnableResetButton(!!hasFilters);
  }, [
    filterQuery,
    firstName,
    lastName,
    membershipNumber,
    dateOfBirth,
    hasExtendedFieldFilters,
    activeFilters,
  ]);

  useEffect(() => {
    if (deleteMemberRequest.result) {
      if (deleteMemberRequest.result.ok) {
        showSuccessToast("Member deleted.");
        refresh();
      } else {
        showErrorToast(
          deleteMemberRequest.result.message ?? "Failed to delete member."
        );
      }
    }
  }, [deleteMemberRequest.result, refresh]);

  const clearAllFilters = () => {
    // Clear core field filters
    setFirstName("");
    setLastName("");
    setMembershipNumber("");
    setDateOfBirth("");

    // Clear extended field search values
    setExtendedFieldSearchValues({});

    // Clear advanced filter values
    setAdvancedFilterValues({});

    // Remove unpinned filters from active filters
    setActiveFilters(new Set(pinnedFilters));

    // Clear the old filter query
    setFilterQuery(undefined);
  };

  const parseAdvancedFilters = (
    filterQuery: string
  ): {
    filterType: string;
    label: string;
    count: number;
    queries: string[];
  }[] => {
    if (!filterQuery) return [];

    const startIndex = filterQuery.indexOf("?");
    if (startIndex === -1) return [];

    const queries = filterQuery.substring(startIndex + 1).split("&");
    const filterGroups: {
      [key: string]: { label: string; queries: string[] };
    } = {};

    for (const query of queries) {
      const equalIndex = query.indexOf("=");
      if (equalIndex === -1) continue;

      const key = query.substr(0, equalIndex);
      const value = query.substr(equalIndex + 1);

      // Group filters by type, combining include and exclude
      let filterType = "";
      let label = "";

      switch (key) {
        case "Financial":
          filterType = "Financial";
          label = "Financial";
          break;
        case "Status":
        case "ExcludeState":
          filterType = "Status";
          label = "Status";
          break;
        case "BirthMonth":
        case "ExcludeBirthMonth":
          filterType = "BirthMonth";
          label = "Birth Month";
          break;
        case "Gender":
        case "ExcludeGender":
          filterType = "Gender";
          label = "Gender";
          break;
        case "MemberLevel":
        case "ExcludeMemberLevel":
          filterType = "MemberLevel";
          label = "Member Level ID";
          break;
        case "MemberLevelDescription":
        case "ExcludeMemberLevelDescription":
          filterType = "MemberLevelDescription";
          label = "Member Level";
          break;
        case "MembershipType":
        case "ExcludeMembershipType":
          filterType = "MembershipType";
          label = "Membership Type ID";
          break;
        case "MembershipTypeDescription":
        case "ExcludeMembershipTypeDescription":
          filterType = "MembershipTypeDescription";
          label = "Membership Type";
          break;
        case "PointsBalanceEqualTo":
        case "PointsBalanceNotEqualTo":
        case "PointsBalanceGreaterThan":
        case "PointsBalanceLessThan":
        case "PointsBalanceGreaterThanOrEqualTo":
        case "PointsBalanceLessThanOrEqualTo":
          filterType = "PointsBalance";
          label = "Points Balance";
          break;
        default:
          filterType = key;
          label = key;
      }

      if (!filterGroups[filterType]) {
        filterGroups[filterType] = { label, queries: [] };
      }
      filterGroups[filterType].queries.push(query);
    }

    return Object.entries(filterGroups).map(([filterType, group]) => ({
      filterType,
      label: group.label,
      count: group.queries.length,
      queries: group.queries,
    }));
  };

  const advancedFilters = parseAdvancedFilters(filterQuery || "");

  // Helper function to render just the input control for an extended field (no wrapper, no label)
  const renderExtendedFieldInputControl = (
    fieldDef: CherryPayApi.ExtendedFieldDefinition
  ) => {
    const value = extendedFieldSearchValues[fieldDef.id] || "";

    const handleChange = (newValue: string | string[]) => {
      setExtendedFieldSearchValues((prev) => ({
        ...prev,
        [fieldDef.id]: newValue,
      }));
    };

    switch (fieldDef.FieldType) {
      case "Date":
        return (
          <FormControl>
            <Input
              type="date"
              value={value as string}
              onChange={(e) => handleChange(e.target.value)}
              backgroundColor="#fff"
              shadow="sm"
              fontSize="xs"
              width="100%"
            />
          </FormControl>
        );

      case "DateTime":
        return (
          <FormControl>
            <Input
              type="datetime-local"
              value={value as string}
              onChange={(e) => handleChange(e.target.value)}
              backgroundColor="#fff"
              shadow="sm"
              fontSize="xs"
              width="100%"
            />
          </FormControl>
        );

      case "Time":
        return (
          <Input
            type="time"
            value={value as string}
            onChange={(e) => handleChange(e.target.value)}
            fontSize="xs"
          />
        );

      case "Number":
      case "NumberSelection":
      case "NumberMultiSelection":
        // Check if it has allowed values
        if (
          fieldDef.NumberAttributes?.AllowedValues &&
          fieldDef.NumberAttributes.AllowedValues.length > 0
        ) {
          const selectOptions = fieldDef.NumberAttributes.AllowedValues.map(
            (val) => ({
              label: val.toString(),
              value: val.toString(),
            })
          );

          const selectedValues = Array.isArray(value)
            ? value
            : value
            ? [value]
            : [];
          const selectedOptions = selectedValues.map((v) => ({
            label: v.toString(),
            value: v.toString(),
          }));

          return (
            <ReactSelect
              isMulti
              name={`extended-field-${fieldDef.id}`}
              value={selectedOptions}
              onChange={(newValue: any) => {
                const values = newValue
                  ? newValue.map((v: any) => v.value)
                  : [];
                handleChange(values);
              }}
              options={selectOptions}
              className="chakra-react-select"
              classNamePrefix="chakra-react-select"
              styles={{
                control: (base) => ({
                  ...base,
                  fontSize: "14px",
                  minHeight: "32px",
                }),
              }}
            />
          );
        }
        // Regular number input
        return (
          <Input
            type="number"
            value={value as string}
            onChange={(e) => handleChange(e.target.value)}
            fontSize="xs"
          />
        );

      case "Text":
      case "TextArea":
      case "TextSelection":
      case "TextMultiSelection":
        // Check if it has allowed values
        if (
          fieldDef.StringAttributes?.AllowedValues &&
          fieldDef.StringAttributes.AllowedValues.length > 0
        ) {
          const selectOptions = fieldDef.StringAttributes.AllowedValues.map(
            (val) => ({
              label: val,
              value: val,
            })
          );

          const selectedValues = Array.isArray(value)
            ? value
            : value
            ? [value]
            : [];
          const selectedOptions = selectedValues.map((v) => ({
            label: v.toString(),
            value: v.toString(),
          }));

          return (
            <ReactSelect
              isMulti
              name={`extended-field-${fieldDef.id}`}
              value={selectedOptions}
              onChange={(newValue: any) => {
                const values = newValue
                  ? newValue.map((v: any) => v.value)
                  : [];
                handleChange(values);
              }}
              options={selectOptions}
              className="chakra-react-select"
              classNamePrefix="chakra-react-select"
              styles={{
                control: (base) => ({
                  ...base,
                  fontSize: "14px",
                  minHeight: "32px",
                }),
              }}
            />
          );
        }
        // Regular text input
        return (
          <Input
            type="text"
            value={value as string}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={fieldDef.FieldName ?? ""}
            fontSize="xs"
          />
        );

      case "Toggle":
        return (
          <Select
            value={value as string}
            onChange={(e) => handleChange(e.target.value)}
            fontSize="xs"
            backgroundColor="#fff"
            shadow="sm"
          >
            <option value="">All</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </Select>
        );

      default:
        // Unknown field type - render as text input
        return (
          <Input
            type="text"
            value={value as string}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={fieldDef.FieldName ?? ""}
            fontSize="xs"
          />
        );
    }
  };

  // Helper function to render extended field search input (with wrapper and label)
  const renderExtendedFieldInput = (
    fieldDef: CherryPayApi.ExtendedFieldDefinition
  ) => {
    const commonProps = {
      key: fieldDef.id,
      width: "100%",
      minWidth: "200px",
      maxWidth: "300px",
    };

    return (
      <Box {...commonProps}>
        <FormLabel mb="1">{fieldDef.FieldName}</FormLabel>
        {renderExtendedFieldInputControl(fieldDef)}
      </Box>
    );
  };

  const removeAdvancedFilterGroup = (queriesToRemove: string[]) => {
    if (!filterQuery) return;

    const startIndex = filterQuery.indexOf("?");
    if (startIndex === -1) return;

    const queries = filterQuery.substring(startIndex + 1).split("&");
    const remainingQueries = queries.filter(
      (query) => !queriesToRemove.includes(query)
    );

    if (remainingQueries.length === 0) {
      setFilterQuery(undefined);
    } else {
      setFilterQuery("?" + remainingQueries.join("&"));
    }
  };

  // Handler for filter selection confirmation
  const handleFilterSelectionConfirm = (selectedFilters: Set<string>) => {
    // Find newly added filters (in selectedFilters but not in activeFilters)
    const newFilters = Array.from(selectedFilters).filter(
      (filterId) => !activeFilters.has(filterId)
    );

    // If there are new filters, ask if user wants to pin them
    if (newFilters.length > 0) {
      setPendingFiltersToAdd(selectedFilters);
      pinConfirmationDialog.onOpen();
    } else {
      // No new filters, just update active filters
      setActiveFilters(selectedFilters);
    }
  };

  // Handler for pin confirmation dialog - Yes
  const handlePinConfirmYes = () => {
    setActiveFilters(pendingFiltersToAdd);

    // Find newly added filters
    const newFilters = Array.from(pendingFiltersToAdd).filter(
      (filterId) => !activeFilters.has(filterId)
    );

    // Add new filters to pinned filters
    setPinnedFilters((prev) => {
      const newSet = new Set(prev);
      newFilters.forEach((filterId) => newSet.add(filterId));
      return newSet;
    });

    pinConfirmationDialog.onClose();
  };

  // Handler for pin confirmation dialog - No
  const handlePinConfirmNo = () => {
    setActiveFilters(pendingFiltersToAdd);
    pinConfirmationDialog.onClose();
  };

  // Handler for toggling pin status
  const togglePinFilter = (filterId: string) => {
    setPinnedFilters((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(filterId)) {
        newSet.delete(filterId);
      } else {
        newSet.add(filterId);
      }
      return newSet;
    });
  };

  // Helper to check if a filter ID is an extended field using regex pattern
  const isExtendedFieldId = (filterId: string): boolean => {
    const extendedFieldPattern = /^\w+\.Ext-(\w+)-\w+-(\w+)(-v(\d+))?$/;
    return extendedFieldPattern.test(filterId);
  };

  // Handler for removing a filter
  const removeFilter = (filterId: string) => {
    setActiveFilters((prev) => {
      const newSet = new Set(prev);
      newSet.delete(filterId);
      return newSet;
    });

    // Also clear the filter value
    if (isExtendedFieldId(filterId)) {
      setExtendedFieldSearchValues((prev) => {
        const currentValue = prev[filterId];
        // Clear by setting to empty string or empty array based on current type
        const newValue = Array.isArray(currentValue) ? [] : "";
        return {
          ...prev,
          [filterId]: newValue,
        };
      });
    } else {
      // Clear advanced filter value
      setAdvancedFilterValues((prev: any) => {
        const newValues = { ...prev };
        delete newValues[filterId];
        return newValues;
      });
    }
  };

  // Helper to get options for advanced filter
  const getAdvancedFilterOptions = (filterId: string) => {
    switch (filterId) {
      case "status":
        return statusOptions || undefined;
      case "birthMonth":
        return birthMonthOptions || undefined;
      case "gender":
        return genderOptions || undefined;
      case "memberLevel":
        return memberLevelOptions || undefined;
      case "memberLevelDescription":
        return memberLevelDescriptionOptions || undefined;
      case "membershipType":
        return membershipTypeOptions || undefined;
      case "membershipTypeDescription":
        return membershipTypeDescriptionOptions || undefined;
      default:
        return undefined;
    }
  };

  const memberRows = useMemo(
    () =>
      members?.map((member) => {
        const memberId = member.id;
        const memberLink = `/businesses/${businessId}/members/${memberId}`;
        return (
          <Tr key={memberId}>
            <Td>
              <Link to={memberLink}>
                {emptyStrToNull(member.MembershipNumber) ??
                  emptyStrToNull(member.BadgeNumber) ??
                  "-"}
              </Link>
            </Td>
            <Td>
              <Link to={memberLink}>
                {(member.FirstName + " " + member.LastName).trim() == ""
                  ? "-"
                  : member.FirstName + " " + member.LastName}
              </Link>
            </Td>
            <Td>
              {DateUtil.formatDate(member.BirthDate, "en-AU", {
                dateStyle: "medium",
              }) ?? "-"}
            </Td>
            <Td>{member.Status ?? "-"}</Td>
            <Td>
              {DateUtil.formatDate(member.DateFinancial, "en-AU", {
                dateStyle: "medium",
              }) ?? "-"}
            </Td>
            <Td>{member.MemberLevelDescription ?? "-"}</Td>
            <Td>{member.MembershipTypeDescription ?? "-"}</Td>
            <Td width="0.1%" whiteSpace="nowrap">
              <Menu>
                <MenuButton
                  icon={<MoreHorizontal />}
                  variant="ghost"
                  as={IconButton}
                  size="sm"
                  w="100%"
                  fontSize="sm"
                  aria-label="Member Options"
                ></MenuButton>
                <MenuList>
                  <MenuItem as={Link} to={memberLink} icon={<UserIcon />}>
                    View Member
                  </MenuItem>
                  {business.LicencedCherryHubProducts?.includes(
                    "HubMembership"
                  ) && (
                    <MenuItem
                      onClick={() => onDeleteMember(member)}
                      icon={<TrashIcon />}
                    >
                      Delete Member
                    </MenuItem>
                  )}
                  {canInviteMemberToApp && (
                    <MenuItem
                      onClick={() =>
                        invitationModalState.onOpen({
                          memberId,
                          type: "digital-membership",
                        })
                      }
                      icon={<CardIcon />}
                    >
                      Digital Membership Card
                    </MenuItem>
                  )}
                  {canInviteMemberToCard && (
                    <>
                      <MenuItem
                        onClick={() =>
                          invitationModalState.onOpen({
                            memberId,
                            type: "cherry-pay-card",
                            cpcType: "instantgift",
                          })
                        }
                        icon={<CardIcon />}
                      >
                        CherryPay Instant Gift Card
                      </MenuItem>
                      <MenuItem
                        onClick={() =>
                          invitationModalState.onOpen({
                            memberId,
                            type: "cherry-pay-card",
                            cpcType: "reloadable",
                          })
                        }
                        icon={<CardIcon />}
                      >
                        CherryPay Reloadable Card
                      </MenuItem>
                    </>
                  )}
                  {canTransferPointsToMoney && (
                    <MenuItem
                      onClick={() =>
                        addPointsModalState.onOpen({
                          memberId,
                          type: "transfer-points-to-cpc",
                        })
                      }
                      icon={<TransferIcon />}
                    >
                      Transfer Points
                    </MenuItem>
                  )}
                  {canAwardPoints && (
                    <MenuItem
                      onClick={() =>
                        addPointsModalState.onOpen({
                          memberId,
                          type: "add-points",
                        })
                      }
                      icon={<PlusIcon />}
                    >
                      Award Points
                    </MenuItem>
                  )}
                </MenuList>
              </Menu>
            </Td>
          </Tr>
        );
      }),
    [members]
  );

  return (
    <>
      <VStack width="100%" spacing="2">
        <Card width="100%">
          <VStack width="100%" spacing="4" alignItems="start">
            <Box
              width="100%"
              bg="white"
              borderRadius="lg"
              p="6"
              shadow="sm"
              border="1px solid"
              borderColor="gray.200"
            >
              <VStack width="100%" spacing="4" alignItems="start">
                {/* Header with collapse toggle and New Member button */}
                <HStack width="100%" justify="space-between">
                  <Button
                    leftIcon={
                      isFilterSectionCollapsed ? (
                        <ChevronDownIcon />
                      ) : (
                        <ChevronUpIcon />
                      )
                    }
                    variant="ghost"
                    onClick={() =>
                      setIsFilterSectionCollapsed(!isFilterSectionCollapsed)
                    }
                    size="sm"
                  >
                    {isFilterSectionCollapsed ? "Show" : "Hide"} Filters
                  </Button>
                  {business.LicencedCherryHubProducts?.includes(
                    "HubMembership"
                  ) && (
                    <Button
                      leftIcon={<UserPlusIcon />}
                      onClick={() =>
                        navigate(`/businesses/${businessId}/members/new`)
                      }
                      colorScheme="cherryButton"
                    >
                      New Member
                    </Button>
                  )}
                </HStack>

                {/* Collapsible filter section */}
                <Collapse
                  in={!isFilterSectionCollapsed}
                  style={{ width: "100%" }}
                >
                  <VStack width="100%" spacing="4" alignItems="start">
                    <Wrap width="100%" spacing="4">
                      <Box width="100%" minWidth="200px" maxWidth="300px">
                        <FormLabel mb="1">Member Number</FormLabel>
                        <Input
                          value={membershipNumber}
                          onChange={onMembershipNumberChange}
                          placeholder="Member Number"
                          fontSize="xs"
                        />
                      </Box>
                      <Box width="100%" minWidth="200px" maxWidth="300px">
                        <FormLabel mb="1">First Name</FormLabel>
                        <Input
                          value={firstName}
                          onChange={onFirstNameChange}
                          placeholder="First Name"
                          fontSize="xs"
                        />
                      </Box>
                      <Box width="100%" minWidth="200px" maxWidth="300px">
                        <FormLabel mb="1">Last Name</FormLabel>
                        <Input
                          value={lastName}
                          onChange={onLastNameChange}
                          placeholder="Last Name"
                          fontSize="xs"
                        />
                      </Box>
                      <Box width="100%" minWidth="200px" maxWidth="300px">
                        <FormLabel mb="1">Date of Birth</FormLabel>
                        <FormControl>
                          <Input
                            type="date"
                            value={dateOfBirth}
                            onChange={(e) => setDateOfBirth(e.target.value)}
                            backgroundColor="#fff"
                            shadow="sm"
                            fontSize="xs"
                            width="100%"
                          />
                        </FormControl>
                      </Box>

                      {/* Dynamic Filters - Render active filters */}
                      {Array.from(activeFilters).map((filterId) => {
                        // Handle extended fields
                        if (isExtendedFieldId(filterId)) {
                          const fieldDef = extendedFieldDefinitions?.find(
                            (f) => f.id === filterId
                          );
                          if (!fieldDef) return null;

                          return (
                            <Box
                              key={filterId}
                              width="100%"
                              minWidth="200px"
                              maxWidth="300px"
                              position="relative"
                            >
                              <HStack justify="space-between" mb="1">
                                <FormLabel mb="0">
                                  {fieldDef.FieldName || fieldDef.id}
                                </FormLabel>
                                <HStack spacing="1">
                                  <IconButton
                                    icon={
                                      pinnedFilters.has(filterId) ? (
                                        <PinFilledIcon />
                                      ) : (
                                        <PinIcon />
                                      )
                                    }
                                    size="xs"
                                    variant="ghost"
                                    aria-label="Pin filter"
                                    onClick={() => togglePinFilter(filterId)}
                                  />
                                  {!pinnedFilters.has(filterId) && (
                                    <IconButton
                                      icon={<DeleteIcon />}
                                      size="xs"
                                      variant="ghost"
                                      aria-label="Remove filter"
                                      onClick={() => removeFilter(filterId)}
                                    />
                                  )}
                                </HStack>
                              </HStack>
                              {renderExtendedFieldInputControl(fieldDef)}
                            </Box>
                          );
                        }

                        // Handle advanced filters
                        return (
                          <AdvancedFilterInput
                            key={filterId}
                            filterId={filterId}
                            label={
                              ADVANCED_FILTER_OPTIONS.find(
                                (f) => f.id === filterId
                              )?.label || filterId
                            }
                            value={advancedFilterValues[filterId]}
                            onChange={(value) =>
                              setAdvancedFilterValues((prev: any) => ({
                                ...prev,
                                [filterId]: value,
                              }))
                            }
                            isPinned={pinnedFilters.has(filterId)}
                            onTogglePin={() => togglePinFilter(filterId)}
                            onRemove={() => removeFilter(filterId)}
                            options={getAdvancedFilterOptions(filterId)}
                          />
                        );
                      })}
                    </Wrap>

                    <HStack width="100%">
                      <HStack flex="1">
                        <Button
                          onClick={clearAllFilters}
                          variant="solid"
                          disabled={!enableResetButton}
                        >
                          Clear All
                        </Button>
                        <Button
                          leftIcon={<PlusIcon />}
                          variant="solid"
                          colorScheme="cherryButton"
                          color="#fff"
                          onClick={() => filterSelectionDialog.onOpen()}
                        >
                          Add Filters
                        </Button>
                        {isLoading && (
                          <Center>
                            <Spinner margin="4" />
                          </Center>
                        )}
                      </HStack>
                    </HStack>
                  </VStack>
                </Collapse>
              </VStack>
            </Box>

            {members && members.length > 0 && (
              <Box w="100%" overflowX="auto">
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Membership No</Th>
                      <Th>Name</Th>
                      <Th>Birth Date</Th>
                      <Th>Status</Th>
                      <Th>Financial Date</Th>
                      <Th>Level</Th>
                      <Th>Membership Type</Th>
                      <Th></Th>
                    </Tr>
                  </Thead>
                  <Tbody>{memberRows}</Tbody>
                </Table>
                {confirmationElement}
              </Box>
            )}
            {!isLoading && members && members.length === 0 && (
              <Text>
                {!firstName &&
                !lastName &&
                !membershipNumber &&
                !dateOfBirth &&
                !filterQuery
                  ? "No members found."
                  : "No members found matching the search criteria."}
              </Text>
            )}

            {fetchNextPage && (
              <Box>
                {!isLoadingPage && (
                  <Button colorScheme="cherryButton" onClick={fetchNextPage}>
                    Load more...
                  </Button>
                )}
                {isLoadingPage && <Spinner />}
              </Box>
            )}

            {error != null && (
              <ErrorMessage>
                An error was encountered while searching members.
              </ErrorMessage>
            )}
          </VStack>
        </Card>
      </VStack>
      <TransferPointsModal
        isOpen={
          addPointsModalState.isOpen &&
          addPointsModalState.opts?.type === "transfer-points-to-cpc"
        }
        memberId={addPointsModalState.opts?.memberId}
        onClose={addPointsModalState.onClose}
      />
      <PointsRequestModal
        isOpen={
          addPointsModalState.isOpen &&
          addPointsModalState.opts?.type === "add-points"
        }
        memberId={addPointsModalState.opts?.memberId}
        pointsRequestType="add-points"
        onClose={addPointsModalState.onClose}
      />
      <InvitationModal
        isOpen={
          invitationModalState.isOpen &&
          invitationModalState.opts?.type === "digital-membership"
        }
        memberId={invitationModalState.opts?.memberId}
        invitationType={invitationModalState.opts?.type}
        onClose={invitationModalState.onClose}
      />
      <CherryPayCardModal
        isOpen={
          invitationModalState.isOpen &&
          invitationModalState.opts?.type === "cherry-pay-card"
        }
        cpcType={invitationModalState.opts?.cpcType ?? "instantgift"}
        memberId={invitationModalState.opts?.memberId}
        onClose={invitationModalState.onClose}
      />
      <FilterSelectionDialog
        isOpen={filterSelectionDialog.isOpen}
        onClose={filterSelectionDialog.onClose}
        advancedFilters={ADVANCED_FILTER_OPTIONS}
        extendedFields={extendedFieldDefinitions || []}
        selectedFilters={activeFilters}
        onConfirm={handleFilterSelectionConfirm}
      />
      <Modal isOpen={pinConfirmationDialog.isOpen} onClose={handlePinConfirmNo}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Pin Filters?</ModalHeader>
          <ModalBody>
            <Text>
              Would you like to pin the newly added filters, which will ensure
              they remain even after clearing filter values?
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="solid" mr={3} onClick={handlePinConfirmNo}>
              No
            </Button>
            <Button colorScheme="cherryButton" onClick={handlePinConfirmYes}>
              Yes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
